import { useQuery } from '@common/hooks';
import { Col, DatePicker, Form, Row } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { ErrorHandler, InfoTooltip, LoadingState } from '@common/components';
import { ASSIGNMENT_TERM_QUERY } from './edit-assignment-term-helper';

export function EditAssignmentForm({
  assignmentTermId,
  form,
  onSubmit,
  isExtension,
  activeTermsEffectiveDates,
}) {
  const { error, data, loading } = useQuery(ASSIGNMENT_TERM_QUERY, {
    variables: { id: assignmentTermId },
  });

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="EditAssignmentModal" />;
  }

  const assignmentTermData = data.assignmentTerm;
  const {
    startDate,
    endDate,
    canEditStartDate,
    canEditEndDateToPast,
    canEditEndDateToFuture,
    staffingRequestAssignment: { state: assignmentState },
  } = assignmentTermData;

  const initialValues = {
    startDate: moment(startDate),
    endDate: moment(endDate),
  };

  const disabledEndDate = (currentDate) => {
    const startRange = canEditEndDateToPast
      ? form.getFieldValue('startDate')
      : moment(endDate);

    const endRange = canEditEndDateToFuture
      ? moment(endDate).add(99, 'year')
      : moment(endDate);

    return (
      !startRange?.isBefore(currentDate) ||
      !endRange?.isSameOrAfter(currentDate)
    );
  };

  const endDateTooltipMessage = () => {
    if (!canEditEndDateToPast && !canEditEndDateToFuture) {
      return 'End date cannot be changed when there is a pending or accepted Extension. Contact Support for assistance.';
    }

    if (!isExtension && assignmentState === 'working') {
      return 'This assignment is in Working state. To extend the end date, use Extend Assignment.';
    }
  };

  const endDateTooltip = () => {
    const message = endDateTooltipMessage();

    return (
      message && (
        <Col>
          <InfoTooltip title={message} leftOffset="xl" />
        </Col>
      )
    );
  };

  function endDateFieldValidator(_, selectedEndDate) {
    const anyTermEffectiveDateAfterSelectedEndDate =
      activeTermsEffectiveDates.some((rawEffectiveDate) => {
        const effectiveDate = moment(rawEffectiveDate);

        return effectiveDate >= selectedEndDate;
      });

    return anyTermEffectiveDateAfterSelectedEndDate
      ? Promise.reject()
      : Promise.resolve();
  }

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues}
        className="EditAssignmentModalForm"
        onFinish={(values) => onSubmit(values)}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              className="form-item-layout"
              labelAlign="left"
              labelCol={{ span: 8 }}
              label="Start Date"
            >
              <Row align="middle">
                <Col span={20}>
                  <Form.Item
                    name="startDate"
                    noStyle
                    required
                    rules={[
                      {
                        required: true,
                        message: 'Please select a Start date',
                      },
                    ]}
                  >
                    <DatePicker
                      picker="date"
                      placeholder="mm/dd/yyyy"
                      format="MM/DD/YYYY"
                      disabled={!canEditStartDate}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              className="form-item-layout"
              labelAlign="left"
              labelCol={{ span: 8 }}
              label="End Date"
            >
              <Row align="middle">
                <Col span={20}>
                  <Form.Item
                    name="endDate"
                    noStyle
                    required
                    rules={[
                      {
                        required: true,
                        message: 'Please select an end date',
                      },
                      {
                        validator: endDateFieldValidator,
                        message:
                          'End date cannot be set before the start of the following assignment term',
                      },
                    ]}
                  >
                    <DatePicker
                      picker="date"
                      placeholder="mm/dd/yyyy"
                      format="MM/DD/YYYY"
                      disabled={
                        !canEditEndDateToPast && !canEditEndDateToFuture
                      }
                      disabledDate={disabledEndDate}
                    />
                  </Form.Item>
                </Col>
                {endDateTooltip()}
              </Row>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <style jsx>{`
        :global(.EditAssignmentModalForm .ant-picker) {
          width: 100%;
        }
        :global(.EditAssignmentModalForm .form-item-layout) {
          margin-bottom: 8px;
        }
      `}</style>
    </>
  );
}

EditAssignmentForm.propTypes = {
  assignmentTermId: PropTypes.string.isRequired,
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isExtension: PropTypes.bool.isRequired,
  activeTermsEffectiveDates: PropTypes.arrayOf(PropTypes.string),
};
